import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthenticationService, DefaultsService, ServiceBase } from '../index';
import { Customer, ProductType, Settings } from '../../models';
import { SettingsService } from '..';
import { Subject } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { SelectableClient } from '../../models/selectable-client';
import { ProductTypeSortOption } from '../../models/product-type-sort.model';
import { SortDataType } from '../../models/sort-data-type.enum';
import { SortOrder } from '../../models/sort-order.enum';
import { FiltersService } from 'src/app/filters/filters.service';
import { MetricsService } from './metrics.service';

@Injectable()
export class ProfileService extends MetricsService {
  customers?: Array<Customer> = undefined;
  customersLoaded = new Subject<Array<Customer>>();
  clientsLoaded = new Subject<Array<SelectableClient>>();
  searchedCustomers?: Array<Customer> = undefined;
  searchedClients?: Array<SelectableClient> = undefined;
  clientSearchTerm?: string = undefined;
  nextCustomerPage: number = 1;
  nextSearchedCustomerPage: number = 1;
  pageSize: number = 200;
  searchTerm: string;
  isSearchingCustomers: boolean = false;
  isSearchingClients: boolean = false;
  useCustomerSearchResults: boolean = false;
  customerSettingsKey: string = 'selectedCustomers';
  branchesSettingsKey: string = 'selectedBranches';
  productSortTypeSettingsKey: string = 'selectedProductSortTypeId';
  regionsSettingsKey: string = 'selectedRegions';
  districtsSettingsKey: string = 'selectedDistricts';
  useGeoLocationSettingsKey: string = 'useGeoLocation';
  zipCodeSettingsKey: string = 'zipCode';
  selectedCustomersChanged = new Subject<Array<Customer>>();
  selectedCustomers: Array<Customer> = [];
  public productSortOptions: Array<ProductTypeSortOption> = [];

  constructor(
    public http: HttpClient,
    public settingsService: SettingsService,
    public authenticationService: AuthenticationService,
    public translateService: TranslateService,
    public defaultsService: DefaultsService,
    public filtersService: FiltersService
  ) {
    super(
      translateService,
      filtersService,
      defaultsService,
      authenticationService
    );
    this.createProductSortOptions();
  }

  private createProductSortOptions(): void {
    this.productSortOptions.push({
      id: 'revenue_highest',
      field: 'revenue_total',
      direction: SortOrder.DESC,
      dataType: SortDataType.number,
    });
    this.productSortOptions.push({
      id: 'revenue_lowest',
      field: 'revenue_total',
      direction: SortOrder.ASC,
      dataType: SortDataType.number,
    });
    this.productSortOptions.push({
      id: 'physical_utilization_highest',
      field: 'physical_utilization_current',
      direction: SortOrder.DESC,
      dataType: SortDataType.number,
    });
    this.productSortOptions.push({
      id: 'physical_utilization_lowest',
      field: 'physical_utilization_current',
      direction: SortOrder.ASC,
      dataType: SortDataType.number,
    });
    this.productSortOptions.push({
      id: 'available_assets_highest',
      field: 'unit_count_current_available',
      direction: SortOrder.DESC,
      dataType: SortDataType.number,
    });
    this.productSortOptions.push({
      id: 'available_assets_lowest',
      field: 'unit_count_current_available',
      direction: SortOrder.ASC,
      dataType: SortDataType.number,
    });
    this.productSortOptions.push({
      id: 'cat_class_highest',
      field: 'cat_class',
      direction: SortOrder.ASC,
      dataType: SortDataType.string,
    });
    this.productSortOptions.push({
      id: 'cat_class_lowest',
      field: 'cat_class',
      direction: SortOrder.DESC,
      dataType: SortDataType.string,
    });
    this.productSortOptions.push({
      id: 'description_highest',
      field: 'product_type_name',
      direction: SortOrder.ASC,
      dataType: SortDataType.string,
    });
    this.productSortOptions.push({
      id: 'description_lowest',
      field: 'product_type_name',
      direction: SortOrder.DESC,
      dataType: SortDataType.string,
    });
  }

  updateProductSortSelection = (sortOption: any) => {
    this.updateSelectedProductSortTypeId(sortOption.id);
  };

  getSelectedBranchIds = (list?: number[]) => {
    if (list !== undefined) {
      return list;
    }
    return [
      ...this.settingsService.getAllSettings(
        this.authenticationService.userInfoView.SelectedClient.ClientID
      ).selectedBranches,
    ];
  };

  getSelectedDistrictIds = (list?: number[]) => {
    if (list !== undefined) {
      return list;
    }
    const settings = this.settingsService.getAllSettings(
      this.authenticationService.userInfoView.SelectedClient.ClientID
    );
    return [...settings.selectedDistricts];
  };

  getSelectedRegionIds = (list?: number[]) => {
    if (list !== undefined) {
      return list;
    }
    return [
      ...this.settingsService.getAllSettings(
        this.authenticationService.userInfoView.SelectedClient.ClientID
      ).selectedRegions,
    ];
  };

  getSelectedCustomers = (): Array<Customer> => {
    return this.selectedCustomers;
  };

  getSelectedProductSortType = () => {
    const sortType = this.settingsService.getAllSettings(
      this.authenticationService.userInfoView.SelectedClient.ClientID
    ).selectedProductSortTypeId;
    if (sortType) {
      const result = this.productSortOptions.filter((r) => r.id === sortType);
      if (result.length) {
        return result[0];
      }
    }
    return null;
  };

  getCachedSettings = () => {
    return {
      ...this.settingsService.getAllSettings(
        this.authenticationService.userInfoView.SelectedClient.ClientID
      ),
    };
  };
  updateSelectedBranches = (branches: any) => {
    this.settingsService.setValue(
      this.authenticationService.userInfoView.SelectedClient.ClientID,
      this.branchesSettingsKey,
      [...branches]
    );
  };

  updateSelectedProductSortTypeId = (selectedSortTypeId: string) => {
    this.settingsService.setValue(
      this.authenticationService.userInfoView.SelectedClient.ClientID,
      this.productSortTypeSettingsKey,
      selectedSortTypeId
    );
  };

  updateSelectedRegions = (regions: any) => {
    this.settingsService.setValue(
      this.authenticationService.userInfoView.SelectedClient.ClientID,
      this.regionsSettingsKey,
      [...regions]
    );
  };

  updateSelectedDistricts = (branches: any) => {
    this.settingsService.setValue(
      this.authenticationService.userInfoView.SelectedClient.ClientID,
      this.districtsSettingsKey,
      [...branches]
    );
  };

  updateUseGeoLocation = (val: boolean) => {
    this.settingsService.setValue(
      this.authenticationService.userInfoView.SelectedClient.ClientID,
      this.useGeoLocationSettingsKey,
      val
    );
  };

  updateZipCode = (val: string) => {
    this.settingsService.setValue(
      this.authenticationService.userInfoView.SelectedClient.ClientID,
      this.zipCodeSettingsKey,
      val
    );
  };

  saveCachedSettings = (settings: Settings) => {
    this.settingsService.save(settings);
  };

  clearCache = (clientId: number) => {
    this.customers = undefined;
    this.nextCustomerPage = 1;
    this.settingsService.clearValues(clientId);
  };

  clearCustomers = () => {
    this.selectedCustomers = [];
    this.selectedCustomersChanged.next(this.selectedCustomers);
  };

  clearBranches = () => {
    this.settingsService.setValue(
      this.authenticationService.userInfoView.SelectedClient.ClientID,
      this.branchesSettingsKey,
      []
    );
  };

  clearDistricts = () => {
    this.settingsService.setValue(
      this.authenticationService.userInfoView.SelectedClient.ClientID,
      this.districtsSettingsKey,
      []
    );
  };

  // TODO: Camelcase
  clearregions = () => {
    this.settingsService.setValue(
      this.authenticationService.userInfoView.SelectedClient.ClientID,
      this.regionsSettingsKey,
      []
    );
  };

  isCustomerSelected = (customerId: number) => {
    return (
      this.selectedCustomers.findIndex((r) => r.customer_id === customerId) !==
      -1
    );
  };

  updateCustomerSelection = (customer: Customer) => {
    const index = this.selectedCustomers.findIndex(
      (r) => r.customer_id === customer.customer_id
    );
    if (index !== -1) {
      this.selectedCustomers.splice(index, 1);
    } else {
      this.selectedCustomers.push(customer);
    }
    this.selectedCustomersChanged.next(this.selectedCustomers);
  };

  clearCustomerCache = () => {
    this.searchedCustomers = undefined;
    this.searchTerm = '';
    this.nextSearchedCustomerPage = 1;
    this.customers = undefined;
  };

  clearClientCache = () => {
    this.searchedClients = undefined;
    this.searchTerm = '';
  };

  clearCustomerSearch = () => {
    this.searchedCustomers = undefined;
    this.customersLoaded.next(this.customers || []);
  };

  getSelectedCustomersIds = () => {
    const customerIds: Array<number> = [];
    this.selectedCustomers.forEach((r) => {
      customerIds.push(r.customer_id);
    });
    return customerIds;
  };

  getCustomers = () => {
    const rowmonthFilter = this.defaultsService.filterDefaults?.find(
      (r) => r.CodeName === 'mobile.rate.customer.row.months'
    );
    const body = this.getMetricsPostBody(
      this.getSelectedCustomersIds(),
      this.getSelectedBranchIds(),
      this.getSelectedRegionIds(),
      this.getSelectedDistrictIds()
    );
    body.pageSize = this.pageSize;
    body.page = this.nextCustomerPage;
    if (rowmonthFilter && rowmonthFilter.DefaultValue) {
      body.filters.and.push({
        '>=': [
          rowmonthFilter?.DefaultValue,
          { var: 'months_since_transaction' },
        ],
      });
    }
    const url = `${environment.metricsApiUrl}/mobile-metrics/customers`;
    this.http.post<Array<Customer>>(url, body).subscribe(
      (customers) => {
        if (!this.customers) {
          this.customers = [...customers];
        } else {
          this.customers?.push(...customers);
        }

        this.nextCustomerPage++;
        this.customersLoaded.next(this.customers || []);
      },
      (error) => {
        if (error.status === '401') {
          Sentry.captureException(error);
        }
      }
    );
  };

  acceptCompetitiveDisclaimer = () => {
    const url = `${environment.apiUrl}/mobile-metrics/mqaCompetitionDisclaimer`;
    return this.http.post<any>(url, {}).toPromise();
  };

  searchClients = (searchTerm: string) => {
    this.clearClientCache();
    this.searchTerm = searchTerm;
    this.isSearchingClients = true;
    if (searchTerm && searchTerm.length) {
      this.searchedClients =
        this.authenticationService.getSelectableClients(searchTerm);
      this.isSearchingCustomers = false;
      this.nextSearchedCustomerPage++;
      this.clientsLoaded.next(this.searchedClients);
    } else {
      this.searchedClients = this.authenticationService.getSelectableClients();
      this.clientsLoaded.next(this.searchedClients);
    }
  };

  searchCustomers = (searchTerm: string) => {
    this.clearCustomerCache();
    this.searchTerm = searchTerm;
    this.nextSearchedCustomerPage = 1;
    this.isSearchingCustomers = true;

    const rowmonthFilter = this.defaultsService.filterDefaults?.find(
      (r) => r.CodeName === 'mobile.rate.customer.row.months'
    );
    this.customersLoaded.next([]);
    this.useCustomerSearchResults = true;
    const body = this.getMetricsPostBody(
      this.getSelectedCustomersIds(),
      this.getSelectedBranchIds(),
      this.getSelectedRegionIds(),
      this.getSelectedDistrictIds()
    );
    body.pageSize = this.pageSize;
    body.page = this.nextSearchedCustomerPage;

    if (rowmonthFilter && rowmonthFilter.DefaultValue) {
      body.filters.and.push({
        '>=': [
          rowmonthFilter?.DefaultValue,
          { var: 'months_since_transaction' },
        ],
      });
    }
    if (this.searchTerm && this.searchTerm.length > 0) {
      body.filters.and.push({ in: [searchTerm, { var: 'customer_name' }] });
    }
    const url = `${environment.metricsApiUrl}/mobile-metrics/customers`;
    this.http.post<Array<Customer>>(url, body).subscribe(
      (customers) => {
        this.searchedCustomers = [...customers];
        this.isSearchingCustomers = false;
        this.nextSearchedCustomerPage++;
        this.customersLoaded.next(this.searchedCustomers || []);
      },
      (error) => {
        if (error.status === '401') {
          this.isSearchingCustomers = false;
          Sentry.captureException(error);
        }
      }
    );
  };

  pageSearchedCustomers = () => {
    const rowmonthFilter = this.defaultsService.filterDefaults?.find(
      (r) => r.CodeName === 'mobile.rate.customer.row.months'
    );
    const body = this.getMetricsPostBody(
      this.getSelectedCustomersIds(),
      this.getSelectedBranchIds(),
      this.getSelectedRegionIds(),
      this.getSelectedDistrictIds()
    );
    body.pageSize = this.pageSize;
    body.page = this.nextCustomerPage;
    if (this.searchTerm && this.searchTerm.trim().length > 0) {
      body.filters.and.push({
        in: [this.searchTerm, { var: 'customer_name' }],
      });
    }
    if (rowmonthFilter && rowmonthFilter.DefaultValue) {
      body.filters.and.push({
        '>=': [
          rowmonthFilter?.DefaultValue,
          { var: 'months_since_transaction' },
        ],
      });
    }
    const url = `${environment.metricsApiUrl}/mobile-metrics/customers`;
    this.http.post<Array<Customer>>(url, body).subscribe(
      (customers) => {
        this.searchedCustomers?.push(...customers);
        this.nextSearchedCustomerPage++;
        this.customersLoaded.next(this.searchedCustomers || []);
      },
      (error) => {
        if (error.status === '401') {
          Sentry.captureException(error);
        }
      }
    );
  };
}
